import React, { createContext, useCallback, useContext, useState } from 'react'
import { useSlotBackgroundTask, type SlotTaskTerminalResult } from '../hooks/useSlotBackgroundTask'
import { notification } from '../components/Notifications'
import { formatSlotTaskToast } from '../utils/formatSlotTaskToast'
import type { SlotTaskKind } from '../models/Task'

interface SlotTaskContextValue {
  isTaskRunning: boolean
  taskMessage: string
  /**
   * An array of ids registers a queue: they are polled in order and only the last is reported.
   *
   * `kind` is only for tasks whose completion toast depends on which operation ran — the two bulk
   * section status changes, which report per-section outcomes. Everything else leaves it unset and
   * gets the generic copy.
   */
  setTask: (taskId: string | string[], message: string, successMessage?: string, onComplete?: () => void, kind?: SlotTaskKind) => void
  isBlockedModalOpen: boolean
  openBlockedModal: () => void
  closeBlockedModal: () => void
  checkIfBlocked: () => Promise<boolean>
}

const SlotTaskContext = createContext<SlotTaskContextValue | null>(null)

interface SlotTaskProviderProps {
  slotId: string
  children: React.ReactNode
}

export const SlotTaskProvider: React.FC<SlotTaskProviderProps> = ({ slotId, children }) => {
  const [isBlockedModalOpen, setIsBlockedModalOpen] = useState(false)

  const handleTerminal = useCallback((result: SlotTaskTerminalResult): void => {
    const toast = formatSlotTaskToast(result)
    notification(toast.title, {
      variant: toast.variant,
      subTitle: toast.subTitle,
      persist: toast.variant !== 'success'
    })
  }, [])

  const { isTaskRunning, taskMessage, setTask, checkTaskNow } = useSlotBackgroundTask({
    slotId,
    onTerminal: handleTerminal
  })

  // Instant status check: clears stale running state if already done; opens blocked modal if still running.
  const checkIfBlocked = useCallback(async (): Promise<boolean> => {
    if (!isTaskRunning) return false
    const stillRunning = await checkTaskNow()
    if (stillRunning) setIsBlockedModalOpen(true)
    return stillRunning
  }, [isTaskRunning, checkTaskNow])

  return (
    <SlotTaskContext.Provider value={{
      isTaskRunning,
      taskMessage,
      setTask,
      isBlockedModalOpen,
      openBlockedModal: () => { setIsBlockedModalOpen(true) },
      closeBlockedModal: () => { setIsBlockedModalOpen(false) },
      checkIfBlocked
    }}>
      {children}
    </SlotTaskContext.Provider>
  )
}

const NOOP_CONTEXT: SlotTaskContextValue = {
  isTaskRunning: false,
  taskMessage: '',
  setTask: () => {},
  isBlockedModalOpen: false,
  openBlockedModal: () => {},
  closeBlockedModal: () => {},
  checkIfBlocked: async () => false
}

export const useSlotTaskContext = (): SlotTaskContextValue => {
  return useContext(SlotTaskContext) ?? NOOP_CONTEXT
}
